import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Zap, BookOpen, FileText, Star, CheckCircle, Download } from "lucide-react";

export default function PromoSidebar() {
  const isPaidUser = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      return u && u.plan && u.plan !== 'free';
    } catch { return false; }
  }, []);
  return (
    <aside className="w-72 shrink-0 hidden lg:block">
      <div className="sticky top-6 space-y-4">

        {/* Main CTA — only for free/anonymous users */}
        {!isPaidUser && <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800 rounded-2xl p-5 border border-indigo-500/30">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="text-yellow-300 text-xs font-bold uppercase tracking-wide">Premium Plan</span>
            </div>
            <h3 className="text-white font-black text-xl mb-2 leading-tight">
              Crack TGT/PGT with AI Notes
            </h3>
            <p className="text-indigo-200 text-xs mb-4 leading-relaxed">
              Get AI-generated notes, unlimited descriptive tests, full question bank access, and PDF downloads.
            </p>
            <div className="space-y-2 mb-4">
              {[
                "AI-generated topic notes",
                "Unlimited descriptive tests",
                "5000+ MCQ practice questions",
                "PDF download for offline study",
                "Performance analytics"
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-3.5 h-3.5 text-green-400 shrink-0" />
                  <span className="text-indigo-100 text-xs">{f}</span>
                </div>
              ))}
            </div>
            <Link
              to="/pro"
              data-testid="promo-get-premium-btn"
              className="block w-full bg-white text-indigo-700 font-black text-sm py-2.5 rounded-xl text-center hover:bg-indigo-50 transition-colors"
            >
              Get Premium → ₹499 one-time
            </Link>
          </div>
        </div>}

        {/* Free vs Paid */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <h4 className="text-white font-bold text-sm mb-3">Free vs Premium</h4>
          <div className="space-y-2.5 text-xs">
            {[
              { feature: "MCQ Practice", free: true, paid: true },
              { feature: "Question Explanations", free: true, paid: true },
              { feature: "AI Topic Notes", free: false, paid: true },
              { feature: "Descriptive Tests", free: false, paid: true },
              { feature: "PDF Downloads", free: false, paid: true },
              { feature: "Performance Tracking", free: false, paid: true },
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 items-center">
                <span className="text-slate-400 col-span-1">{row.feature}</span>
                <span className={`text-center ${row.free ? "text-emerald-400" : "text-slate-600"}`}>
                  {row.free ? "✓" : "✗"}
                </span>
                <span className={`text-center ${row.paid ? "text-emerald-400" : "text-slate-600"}`}>
                  {row.paid ? "✓" : "✗"}
                </span>
              </div>
            ))}
            <div className="grid grid-cols-3 gap-2 mt-1 border-t border-white/10 pt-2">
              <span className="text-slate-500"></span>
              <span className="text-center text-slate-500 text-xs">Free</span>
              <span className="text-center text-indigo-400 text-xs font-bold">Premium</span>
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" /> Why Descriptive Test?
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {[
              { val: "10,000+", label: "Questions" },
              { val: "6", label: "Exams Covered" },
              { val: "Expert", label: "Explanations" },
              { val: "Free", label: "Always" },
            ].map((s, i) => (
              <div key={i} className="bg-white/5 rounded-lg p-2 text-center">
                <div className="text-indigo-400 font-black text-sm">{s.val}</div>
                <div className="text-slate-500 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* App Download */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="text-white font-bold text-sm">Get the App</h4>
              <p className="text-slate-400 text-xs mt-0.5">Study on mobile, anytime anywhere</p>
              <Link
                to="/"
                className="inline-block mt-2 text-xs text-indigo-400 hover:text-indigo-300 border border-indigo-500/30 rounded-lg px-3 py-1.5 transition-colors"
              >
                Download Free →
              </Link>
            </div>
          </div>
        </div>

      </div>
    </aside>
  );
}
